import { useEscapeKeyClear } from './useEscapeKeyClear';
import { useGridMouseHandlers } from './useGridMouseHandlers';
import { usePlanningActionHandler } from './usePlanningActionHandler';
import type { PlanEditorProps } from './PlanEditor.types';
import type { GridSelectionRect, BulkPlacementPreview } from './types';
import type { PlanAppMode, PlanningSelectionInfo } from '../../../types/planManagement';
import type { EntityLookup } from '../../../utils/planEntities';

interface UsePlanEditorInteractionsParams {
  props: PlanEditorProps;
  appMode: PlanAppMode;
  isDragging: boolean;
  isEraseMode: boolean;
  isBulkPlacementMode: boolean;
  planningSelection: PlanningSelectionInfo | null;
  selectionStart: { row: number; col: number } | null;
  selectionEnd: { row: number; col: number } | null;
  selectionRect: GridSelectionRect | null;
  bulkPlacementPreview: BulkPlacementPreview | null;
  entityLookup: EntityLookup;
  entityState: any;
  entityHandlers: any;
  analyzePlanningSelection: (minRow: number, maxRow: number, minCol: number, maxCol: number) => PlanningSelectionInfo;
  handleConfirmBulkPlacement: () => void;
  state: any;
}

export const usePlanEditorInteractions = (params: UsePlanEditorInteractionsParams) => {
  useEscapeKeyClear({
    hasPlanningSelection: Boolean(params.planningSelection),
    hasSelectionDrag: Boolean(params.selectionStart || params.selectionEnd),
    hasEntitySelection: Boolean(params.entityState.selectedEntity),
    hasMultiSelection: params.state.multiSelectedBeds.size > 0 || params.state.multiSelectedPlots.size > 0,
    clearPlanningSelection: () => params.state.setPlanningSelection(null),
    clearSelectionDrag: params.state.clearDragSelection,
    clearEntitySelection: params.entityHandlers.clearSelection,
    clearMultiSelection: params.state.clearMultiSelection,
  });

  const { handleMouseDown, handleMouseEnter } = useGridMouseHandlers({
    appMode: params.appMode,
    allowBedSelection: params.props.allowBedSelection ?? false,
    isBusy: params.props.isBusy ?? false,
    isDragging: params.isDragging,
    isEraseMode: params.isEraseMode,
    isBulkPlacementMode: params.isBulkPlacementMode,
    bulkPlacementPreview: params.bulkPlacementPreview,
    entityLookup: params.entityLookup,
    onPatch: params.props.onPatch,
    onPlotClick: params.props.onPlotClick,
    onEmptyCellClick: params.props.onEmptyCellClick,
    handleConfirmBulkPlacement: params.handleConfirmBulkPlacement,
    clearEntitySelection: params.entityHandlers.clearSelection,
    onHoverCell: params.entityHandlers.handleCellHover,
    onCellClick: params.entityHandlers.handleCellClick,
    setPlanningSelection: params.state.setPlanningSelection,
    setSelectionStart: params.state.setSelectionStart,
    setSelectionEnd: params.state.setSelectionEnd,
    setIsDragging: params.state.setIsDragging,
    setMultiSelectedBeds: params.state.setMultiSelectedBeds,
    setMultiSelectedPlots: params.state.setMultiSelectedPlots,
    setSelectedBedForPlanting: params.state.setSelectedBedForPlanting,
    setPlantingModalVisible: params.state.setPlantingModalVisible,
    setBulkPlacementHoverPos: params.state.setBulkPlacementHoverPos,
    onManagementPlotAction: params.props.onManagementPlotAction,
    onManagementBedClick: params.props.onManagementBedClick,
  });

  const handleMouseLeave = () => {
    if (params.isBulkPlacementMode) return params.state.setBulkPlacementHoverPos(null);
    params.entityHandlers.handleCellHoverEnd();
  };

  const handleMouseUp = () => {
    if (params.appMode === 'planning' && params.props.allowBedSelection && params.isDragging && params.selectionRect && !params.selectionRect.exceedsBoundary) {
      const analysis = params.analyzePlanningSelection(
        params.selectionRect.minRow,
        params.selectionRect.maxRow,
        params.selectionRect.minCol,
        params.selectionRect.maxCol,
      );
      if (analysis.totalCells > 0) params.state.setPlanningSelection(analysis);
    }
    params.state.setSelectionStart(null);
    params.state.setSelectionEnd(null);
    params.state.setIsDragging(false);
  };

  const handleGridMouseLeave = () => {
    if (params.isDragging && params.appMode === 'planning' && params.props.allowBedSelection) {
      params.state.setSelectionStart(null);
      params.state.setSelectionEnd(null);
    }
    params.state.setIsDragging(false);
    params.entityHandlers.handleCellHoverEnd();
  };

  const handlePlanningAction = usePlanningActionHandler({
    planningSelection: params.planningSelection,
    onBedSelection: params.props.onBedSelection,
    onPatch: params.props.onPatch,
    clearPlanningSelection: () => params.state.setPlanningSelection(null),
  });

  const handleAppModeChange = (newMode: PlanAppMode) => {
    params.state.setAppMode(newMode);
    params.state.setMultiSelectedBeds(new Set());
    params.state.setMultiSelectedPlots(new Set());
    params.entityHandlers.clearSelection();
    params.state.setSelectionStart(null);
    params.state.setSelectionEnd(null);
    params.state.setPlanningSelection(null);
    params.state.setIsEraseMode(false);
  };

  return { handleMouseDown, handleMouseEnter, handleMouseLeave, handleMouseUp, handleGridMouseLeave, handlePlanningAction, handleAppModeChange };
};
